// ---------------------------------------------------------------------------
// BlocksPane — pane "Cálculos" do ProgressDrawerV3.
// ---------------------------------------------------------------------------
// Lista de BlockV3 (snapshot da implementacao) com status atual da
// ultima execucao + duracao. Click em um bloco expande inline para
// mostrar o log dele, renderizado pelo ``BlockLogViewer`` (componente
// novo da V3 — nao reusa o ``SimpleLogViewer`` do V2). Multiplos
// blocos podem ficar abertos ao mesmo tempo, com transicao suave de
// abertura/fechamento (max-height + opacity, mesma tecnica do
// ``InputBoxV3``).
//
// Comparacao entre meses:
//   - vive **dentro do log**, nao mais como toggle global da pane;
//   - cada linha-indicador (label: valor) ganha um pequeno botao
//     "comparar" que abre uma tabela 2D imediatamente abaixo da linha:
//     anos no eixo vertical (A-1, A, A+1) e meses 1..12 no horizontal,
//     ocupando toda a largura disponivel;
//   - janela e fixa por decisao da sub-leva A (sem seletor de
//     periodo); celulas fora do range ``[firstMonth..lastMonth]`` da
//     implementacao renderizam como "—";
//   - sub-leva A usa valores ficticios — banner amarelo no topo do
//     log avisa.
//
// Sub-leva A: ``MOCK_BLOCKS`` / ``MOCK_BLOCK_LOGS``.
// Roadmap (sub-levas C/D):
//   - ``ImplementationBlocksView`` lista blocos + status corrente.
//   - ``BlockLogView`` retorna log_data por bloco (mantem ``raw_output``
//     no curto prazo; vira array estruturado quando ``log.metric()``
//     entrar no SimpleLog do backend).
//   - Comparacao real: backend vai marcar indicadores explicitamente
//     (via ``log.metric()`` ou regex sobre raw_output — decisao na
//     sub-leva D) e devolver a serie historica em uma chamada
//     dedicada.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Tooltip from "@material-ui/core/Tooltip";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import DescriptionOutlined from "@material-ui/icons/DescriptionOutlined";
import AccountTreeOutlined from "@material-ui/icons/AccountTreeOutlined";
import BugReportOutlined from "@material-ui/icons/BugReportOutlined";
import Refresh from "@material-ui/icons/Refresh";
import SummarizeOutlined from "@material-ui/icons/DescriptionOutlined";

import useThemeColors from "../../../../../hooks/useThemeColors";
import useProgressStyles from "../styles";
import BlockLogViewer from "../BlockLogViewer";
import DrawerPaneLoadingLayout, {
  PaneLoadingBody,
} from "../../DrawerPaneLoadingLayout";
import { useOverlayScrollStyles } from "../../styles";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import useBlocksV3 from "../useBlocksV3";
import {
  dispatchBlockV3,
  fetchBlockLogV3,
} from "../../../../../services/inputsV3Service";

// Vocabul\u00e1rio centralizado em ``services/inputsV3StateRules`` (PA-006)
// \u2014 evita duplicar mapeamentos de estado em c\u00f3digos paralelos. Veja a
// se\u00e7\u00e3o "1.d" do arquivo: ``BLOCK_STATUS_LABELS``.
import { BLOCK_STATUS_LABELS as STATUS_LABEL } from "../../../../../services/inputsV3StateRules";

// Backend envia ``last_run_at`` como ISO em UTC; converte para o
// timezone do usuario antes de renderizar. Formato fixo:
// ``DD/MM/YYYY H:MM`` (24h, sem AM/PM, sem zero-pad na hora). Construir
// manualmente em vez de ``toLocaleString`` evita que locales en-US
// devolvam ``MM/DD`` ou hora 12h. Aceita o formato antigo
// ``dd/MM HH:mm`` para compat retroativa caso o backend ainda nao
// tenha sido deployado.
function formatLastRunLocal(value) {
  if (!value) return "";
  if (typeof value !== "string") return String(value);
  if (!value.includes("T")) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function statusClass(classes, status) {
  switch (status) {
    case "done":
      return classes.blockStatusDone;
    // Onda 3.1: bucket canonico ``processing`` (era ``running``).
    case "processing":
    case "running":
      return classes.blockStatusRunning;
    case "failed":
      return classes.blockStatusFailed;
    case "stale":
      return classes.blockStatusStale;
    case "waiting":
    default:
      return classes.blockStatusWaiting;
  }
}

function isBlockDone(status) {
  return status === "done";
}

function parseBlockError(error) {
  if (!error) return "";
  try {
    const parsed = JSON.parse(error);
    return parsed.message || error;
  } catch {
    return error;
  }
}

function formatDepPeriod(dep) {
  if (!dep.year || !dep.month) return null;
  return `${String(dep.month).padStart(2, "0")}/${String(dep.year).slice(-2)}`;
}

function depPeriodKey(dep) {
  if (!dep.year || !dep.month) return null;
  return dep.year * 100 + dep.month;
}

function groupDependencies(dependencies) {
  const groups = new Map();
  (dependencies || []).forEach((dep) => {
    const key = [
      dep.kind || "input",
      dep.metaName || dep.displayName || "?",
      dep.optional ? "optional" : "required",
    ].join("::");
    const prev = groups.get(key) || {
      kind: dep.kind || "input",
      name: dep.displayName || dep.display_name || dep.metaName || "?",
      optional: !!dep.optional,
      periods: [],
    };
    prev.periods.push(dep);
    groups.set(key, prev);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    periods: group.periods.sort((a, b) => {
      const ay = a.year || 0;
      const by = b.year || 0;
      if (ay !== by) return ay - by;
      return (a.month || 0) - (b.month || 0);
    }),
  }));
}

function buildInputLabelByMeta(dependencies) {
  return (dependencies || []).reduce((acc, dep) => {
    if (dep.kind === "input" && dep.metaName) {
      acc[dep.metaName] = dep.displayName || dep.display_name || dep.metaName;
    }
    return acc;
  }, {});
}

function dependencyState(dep) {
  if (dep.displayState === "outside_window") return "outside_window";
  if (dep.displayState === "ready") return "ready";
  // Parse OK porem pergunta humana (input_parse/input_row_edit) em
  // aberto: o block nao pode rodar ate a decisao ser respondida.
  // Tem prioridade visual sobre ``waiting_xlink`` (sinal mais
  // acionavel) e nunca deve aparecer como ``OK``.
  if (dep.displayState === "waiting_decision") return "waiting_decision";
  // Parse OK porem cross-linking ainda em aberto/erro: o block nao
  // pode rodar ate o xlink fechar. Tratamos como ``pending`` no rank
  // visual, mas com label proprio para ficar claro pra que esta
  // esperando.
  if (dep.displayState === "waiting_xlink") return "waiting_xlink";
  if (dep.drifted) return "stale";
  if (dep.state === null || dep.state === undefined) return "missing";
  return "pending";
}

function dependencyLabel(dep) {
  const state = dependencyState(dep);
  // Diretriz de capitalizacao: OK fica OK (sigla); todos os demais
  // labels usam apenas a primeira letra em maiuscula. "xlink" era
  // jargao tecnico — substituido pela descricao de negocio.
  if (state === "ready") return "OK";
  // I-12 (auditoria mai/2026): "Defasado" era jarg\u00e3o desalinhado do
  // vocabul\u00e1rio de UX (tiles do BlocksPane chamam o estado equivalente
  // de "na fila" via ``BLOCK_STATUS_LABELS.stale``). Padronizado para
  // "Na fila" tamb\u00e9m aqui \u2014 mesma sem\u00e2ntica do tile do bloco.
  if (state === "stale") return "Na fila";
  if (state === "outside_window") return "Fora da janela";
  if (state === "missing") return "Ausente";
  if (state === "waiting_decision") {
    return "Pergunta pendente";
  }
  if (state === "waiting_xlink") {
    return "Cruzamento pendente";
  }
  const raw = dep.displayState || dep.state || "pendente";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function buildDependencyColumns(groups, periodCtx) {
  const byKey = new Map();
  groups.forEach((group) => {
    group.periods.forEach((dep) => {
      const label = formatDepPeriod(dep);
      const key = depPeriodKey(dep);
      if (label && key !== null) byKey.set(key, label);
    });
  });
  if (byKey.size === 0 && periodCtx?.currentYear && periodCtx?.currentMonth) {
    byKey.set(
      periodCtx.currentYear * 100 + periodCtx.currentMonth,
      `${String(periodCtx.currentMonth).padStart(2, "0")}/${String(
        periodCtx.currentYear,
      ).slice(-2)}`,
    );
  }
  if (byKey.size === 0) byKey.set(0, "Período");
  return Array.from(byKey.entries())
    .sort(([a], [b]) => a - b)
    .map(([, label]) => label)
    .slice(-6);
}

function DependenciesMatrix({ classes, dependencies, periodCtx }) {
  const groups = groupDependencies(dependencies);
  const columns = buildDependencyColumns(groups, periodCtx);

  if (!groups.length) {
    return (
      <div className={classes.blockLogPanelEmpty}>
        <AccountTreeOutlined />
        <span>Sem dependências declaradas para este bloco.</span>
      </div>
    );
  }

  return (
    <div className={classes.blockDepsMatrix}>
      <div
        className={classes.blockDepsMatrixHead}
        style={{ gridTemplateColumns: `230px repeat(${columns.length}, 1fr)` }}
      >
        <div>Dependência</div>
        {columns.map((col) => (
          <div key={col}>{col}</div>
        ))}
      </div>
      {groups.map((group) => (
        <div
          key={`${group.kind}-${group.name}`}
          className={classes.blockDepsMatrixRow}
          style={{
            gridTemplateColumns: `230px repeat(${columns.length}, 1fr)`,
          }}
        >
          <div className={classes.blockDepsMatrixName}>
            <span className={classes.blockDepTypeChip}>
              {group.kind === "input" ? (
                <DescriptionOutlined />
              ) : (
                <AccountTreeOutlined />
              )}
            </span>
            <strong>{group.name}</strong>
            {group.optional && <small>opcional</small>}
          </div>
          {columns.map((col) => {
            const dep = group.periods.find((p) => formatDepPeriod(p) === col);
            if (!dep) {
              return (
                <div key={col} className={classes.blockDepsMatrixCellEmpty}>
                  —
                </div>
              );
            }
            return (
              <div
                key={col}
                className={`${classes.blockDepsMatrixCell} ${
                  classes[`blockDepsMatrixCell_${dependencyState(dep)}`]
                }`}
              >
                {dependencyLabel(dep)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

DependenciesMatrix.propTypes = {
  classes: PropTypes.object.isRequired,
  dependencies: PropTypes.array,
  periodCtx: PropTypes.object,
};

function resolveBlockLog(block, logs) {
  // Fase 5.1: sem fallback para mocks. Quando o backend ainda nao
  // exposta logs reais para o bloco, devolvemos ``null`` — a UI
  // renderiza o estado vazio "Sem log disponivel" honesto em vez
  // de um log fictício.
  if (!logs) return null;
  return logs[block.id] || logs[block.metaName] || null;
}

const BlocksPane = ({
  blocks,
  blockLogs,
  periodCtx,
  onOpenInput,
  onMonthStatusChange,
  monthStatusHint,
  // Onda 2.0 (mai/2026): override completo vindo do ``useInputsStateV3``
  // do parent (``InputsV3.js``). Quando ``blocksOverride`` esta
  // presente, este pane NAO dispara ``useBlocksV3`` — toda a fonte de
  // verdade (blocks + monthStatus + userIsDev + refetch) vem por prop.
  // Isso elimina o polling paralelo que rodava aqui em 4s e garante
  // sincronia perfeita com o resto da view (single polling do hook
  // unico no parent).
  blocksOverride,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useProgressStyles({ clientColor });
  const overlayScroll = useOverlayScrollStyles();
  const scrollRef = useNativeWheelScroll();

  const overrideActive = !!blocksOverride;

  // Quando o caller nao passa ``blocks`` explicitamente nem o
  // ``blocksOverride`` consolidado, buscamos no backend
  // (``/api/v3/blocks/?year=&month=``). Caminho legado mantido
  // enquanto a migracao para o hook unico nao esta completa em
  // todos os call-sites do ``BlocksPane``.
  const fetched = useBlocksV3({
    year: periodCtx?.currentYear,
    month: periodCtx?.currentMonth,
    enabled: !overrideActive && blocks === undefined,
    onMonthStatusChange,
    monthStatusHint,
  });

  // Onda 2.0: se o override esta presente, dispara
  // ``onMonthStatusChange`` com o ``monthStatus`` canonico vindo do
  // payload consolidado. Mesma semantica do callback dentro de
  // ``useBlocksV3.applyPayload``: avisa o parent para sobrepor
  // ``selectedMonthCanonical`` no mesmo render cycle.
  useEffect(() => {
    if (!overrideActive) return;
    if (typeof onMonthStatusChange !== "function") return;
    const ms = blocksOverride?.monthStatus ?? null;
    if (ms === null) return;
    onMonthStatusChange({
      year: periodCtx?.currentYear,
      month: periodCtx?.currentMonth,
      status: ms,
      reason: blocksOverride?.monthStatusReason || null,
      active: blocksOverride?.monthStatusActive || null,
    });
  }, [
    overrideActive,
    blocksOverride?.monthStatus,
    blocksOverride?.monthStatusReason,
    blocksOverride?.monthStatusActive,
    onMonthStatusChange,
    periodCtx?.currentYear,
    periodCtx?.currentMonth,
  ]);

  let list;
  if (overrideActive) {
    list = blocksOverride?.blocks || [];
  } else if (blocks !== undefined) {
    list = blocks;
  } else if (fetched.error || (!fetched.loading && fetched.blocks.length > 0)) {
    list = fetched.blocks;
  } else if (fetched.loading) {
    list = [];
  } else {
    // Fase 5.1: sem fallback para ``MOCK_BLOCKS``. Se o backend nao
    // declarou blocos para a implementacao corrente, o pane ja
    // renderiza o estado vazio honesto ("Nenhum bloco configurado").
    list = fetched.blocks;
  }
  const logs = blockLogs || null;
  const isLoading = !overrideActive && blocks === undefined && fetched.loading;
  const userIsDev = overrideActive
    ? !!blocksOverride?.userIsDev
    : !!fetched.userIsDev;
  // Sob override, ``refetch`` cai no parent — o hook unico
  // ``useInputsStateV3`` expoe ``refresh()`` que e' encaminhado via
  // prop ``blocksOverride.refetch``. Quando ausente, vira no-op.
  const refetch = overrideActive
    ? blocksOverride?.refetch || (() => {})
    : fetched.refetch;
  const [recalcing, setRecalcing] = useState(() => new Set());
  const [optimisticStatuses, setOptimisticStatuses] = useState(() => ({}));
  // PA-022 — Mostramos a VERDADE de cada bloco: o status vem direto
  // do backend (``BlockControlV3.state``), sem promocoes locais.
  //
  // Versao anterior promovia ``stale``/``pending`` para ``running``
  // sempre que o ``monthStatus`` estivesse em ``processing`` — para
  // alinhar visualmente o tile com o badge do topo. O efeito colateral
  // era pintar VARIOS blocos como "calculando" simultaneamente quando
  // so UM job estava de fato executando no scheduler (regra "um job
  // ativo por implementacao"). O resto da fila aparecia rodando, o
  // que era enganoso.
  //
  // Comportamento atual:
  //   - tile do bloco efetivamente executando: ``processing`` (vem do
  //     DB quando ``start_block_job_v3`` seta ``state="processing"`` —
  //     Onda 3.1, antes ``"running"``).
  //   - demais blocos da fila: ``stale`` (UI: "na fila") ou ``waiting``
  //     se algum dia algo voltar a marcar ``blocked``.
  //   - badge do mes continua ``processing`` enquanto houver job BLOCK
  //     ativo, refletindo "tem trabalho acontecendo no mes" sem
  //     mentir sobre cada bloco individualmente.
  //
  // Mantemos a funcao como passthrough para preservar o ponto de
  // extensao caso queiramos voltar a aplicar regras pontuais no futuro.
  const displayStatusFor = (raw) => raw;
  const [activeBlockTabs, setActiveBlockTabs] = useState(() => ({}));
  const [fetchedLogs, setFetchedLogs] = useState(() => ({}));
  const [loadingLogs, setLoadingLogs] = useState(() => new Set());
  const [staleLogIds, setStaleLogIds] = useState(() => new Set());

  // O status otimista "stale" (= "na fila") cobre o intervalo entre o
  // sucesso do dispatch manual e o proximo refetch confirmar o estado
  // real. Backend persiste o bloco clicado como ``pending`` (mapeado
  // para ``stale`` na UI); PA-021 retirou a propagacao para dependentes
  // — eles ficam com o estado que ja tinham ate o scheduler concluir
  // o recalc e cascadear staleness se o output mudar. O otimista
  // existe apenas para esconder o gap de ~200ms ate o refetch chegar.
  // Saimos do otimista assim que o backend reporta ``running`` ou um
  // estado terminal.
  useEffect(() => {
    setOptimisticStatuses((prev) => {
      let changed = false;
      const next = { ...prev };
      list.forEach((block) => {
        if (
          next[block.id] &&
          block.status !== "waiting" &&
          block.status !== "stale"
        ) {
          delete next[block.id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [list]);

  const handleRecalc = async (block) => {
    if (!periodCtx?.currentYear || !periodCtx?.currentMonth) return;
    setRecalcing((prev) => {
      const next = new Set(prev);
      next.add(block.id);
      return next;
    });
    setFetchedLogs((prev) => {
      const next = { ...prev };
      delete next[block.id];
      return next;
    });
    setStaleLogIds((prev) => {
      const next = new Set(prev);
      next.add(block.id);
      return next;
    });
    try {
      const res = await dispatchBlockV3({
        metaName: block.metaName,
        year: periodCtx.currentYear,
        month: periodCtx.currentMonth,
      });
      // Leva 98: a API agora devolve ``new_status`` ("stale" para
      // "na fila", "waiting" para "aguardando"). Antes disso, o
      // backend sempre marcava ``pending`` mesmo quando faltavam deps
      // — e o tile mostrava "na fila" enganosamente. Agora a
      // reactivity otimista respeita o veredito do backend:
      //   - deps prontas -> tile vai pra "na fila" (stale)
      //   - deps faltando -> tile vai pra "aguardando" (waiting),
      //     sem antecipar ``processing`` no badge do topo (afinal o
      //     bloco nao vai entrar em fila ate as deps chegarem).
      const apiNewStatus =
        res && typeof res.new_status === "string" ? res.new_status : "stale";
      setOptimisticStatuses((prev) => ({
        ...prev,
        [block.id]: apiNewStatus,
      }));
      // Antecipa o badge do topo (e a aba do MonthSelectorV3) para
      // ``processing`` no MESMO render cycle — SO quando o bloco
      // realmente entrou em fila. Para deps faltando ("waiting") nao
      // antecipamos: mes nao virou "processing" porque nao ha job
      // ativo novo, e o canonical do refetch confirmaria isso de
      // qualquer jeito.
      if (
        apiNewStatus !== "waiting" &&
        typeof onMonthStatusChange === "function"
      ) {
        onMonthStatusChange({
          year: periodCtx.currentYear,
          month: periodCtx.currentMonth,
          status: "processing",
        });
      }
      if (refetch) refetch();
    } catch (err) {
      // Silencioso por enquanto; um toast global cobrirá no leva D do
      // error handling. Console mantem rastreabilidade em dev.
      // eslint-disable-next-line no-console
      console.error("dispatchBlockV3 falhou", err);
    } finally {
      setRecalcing((prev) => {
        const next = new Set(prev);
        next.delete(block.id);
        return next;
      });
    }
  };

  const [openIds, setOpenIds] = useState(() => new Set());

  const activeTabFor = (id) => activeBlockTabs[id] || null;
  const toggleActiveTabFor = (id, tab) => {
    setActiveBlockTabs((prev) => {
      const nextTab = prev[id] === tab ? null : tab;
      return { ...prev, [id]: nextTab };
    });
  };

  const ensureLogForBlock = async (block, options = {}) => {
    const force = !!options.force;
    if (
      !block?.metaName ||
      !periodCtx?.currentYear ||
      !periodCtx?.currentMonth ||
      recalcing.has(block.id) ||
      !isBlockDone(optimisticStatuses[block.id] || block.status)
    ) {
      return;
    }
    const hasFetchedLog = Object.prototype.hasOwnProperty.call(
      fetchedLogs,
      block.id,
    );
    if (
      !force &&
      !staleLogIds.has(block.id) &&
      (logs?.[block.id] || logs?.[block.metaName] || hasFetchedLog)
    ) {
      return;
    }
    setLoadingLogs((prev) => {
      const next = new Set(prev);
      next.add(block.id);
      return next;
    });
    try {
      const payload = await fetchBlockLogV3({
        metaName: block.metaName,
        year: periodCtx.currentYear,
        month: periodCtx.currentMonth,
      });
      setFetchedLogs((prev) => ({ ...prev, [block.id]: payload?.log || null }));
      setStaleLogIds((prev) => {
        if (!prev.has(block.id)) return prev;
        const next = new Set(prev);
        next.delete(block.id);
        return next;
      });
    } catch {
      setFetchedLogs((prev) => ({ ...prev, [block.id]: null }));
    } finally {
      setLoadingLogs((prev) => {
        const next = new Set(prev);
        next.delete(block.id);
        return next;
      });
    }
  };

  useEffect(() => {
    list.forEach((block) => {
      const status = optimisticStatuses[block.id] || block.status;
      if (
        openIds.has(block.id) &&
        activeTabFor(block.id) === "log" &&
        isBlockDone(status) &&
        !recalcing.has(block.id) &&
        !loadingLogs.has(block.id)
      ) {
        const hasFetchedLog = Object.prototype.hasOwnProperty.call(
          fetchedLogs,
          block.id,
        );
        if (staleLogIds.has(block.id) || !hasFetchedLog) {
          ensureLogForBlock(block, { force: staleLogIds.has(block.id) });
        }
      }
    });
  }, [
    list,
    openIds,
    activeBlockTabs,
    optimisticStatuses,
    fetchedLogs,
    loadingLogs,
    recalcing,
    staleLogIds,
    periodCtx?.currentYear,
    periodCtx?.currentMonth,
  ]);

  const toggle = (block) => {
    const id = block.id;
    if (!openIds.has(id)) {
      setActiveBlockTabs((prev) => {
        if (!prev[id]) return prev;
        const nextTabs = { ...prev };
        delete nextTabs[id];
        return nextTabs;
      });
    }
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const counts = useMemo(() => {
    // Onda 3.1: bucket canonico ``processing`` (era ``running``). O
    // backend agora envia ``processing`` na chave do summary; mantemos
    // ambos os contadores para nao perder volume durante a transicao
    // (snapshot cacheado de ETag antigo ainda pode trazer ``running``).
    const c = {
      done: 0,
      failed: 0,
      waiting: 0,
      processing: 0,
      running: 0,
      stale: 0,
    };
    list.forEach((b) => {
      const status = displayStatusFor(optimisticStatuses[b.id] || b.status);
      if (c[status] !== undefined) c[status]++;
    });
    return c;
  }, [list, optimisticStatuses]);

  if (isLoading) {
    return (
      <DrawerPaneLoadingLayout
        variant="progress"
        title="Blocos de cálculo"
        message="Carregando blocos de cálculo…"
      />
    );
  }

  if (!list || list.length === 0) {
    return (
      <>
        <div className={classes.paneHead}>
          <h4 className={classes.paneHeadTitle}>Blocos de cálculo</h4>
        </div>
        <div className={classes.paneEmpty}>
          <DescriptionOutlined />
          <div className={classes.paneEmptyTitle}>Nenhum bloco configurado</div>
          <div className={classes.paneEmptySub}>
            A implementação ainda não declarou blocos de cálculo (BlockV3).
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={classes.paneHead}>
        <h4 className={classes.paneHeadTitle}>Blocos de cálculo</h4>
        <span className={classes.paneHeadCount}>
          {list.length} blocos · {counts.done} concluídos
          {counts.failed > 0 ? ` · ${counts.failed} com falha` : ""}
          {counts.stale > 0 ? ` · ${counts.stale} na fila` : ""}
          {counts.waiting > 0 ? ` · ${counts.waiting} aguardando` : ""}
        </span>
      </div>

      <div
        ref={scrollRef}
        className={overlayScroll.overlayScroll}
        style={{ flex: 1, minHeight: 0 }}
      >
        <div className={classes.blocksList}>
          {list.map((b) => {
            const isOpen = openIds.has(b.id);
            const logLoading = loadingLogs.has(b.id);
            const activeTab = activeTabFor(b.id);
            const blockError = parseBlockError(b.error);
            const effectiveStatus = displayStatusFor(
              optimisticStatuses[b.id] || b.status,
            );
            const isRecalculating = recalcing.has(b.id);
            const canShowLog = isBlockDone(effectiveStatus) && !isRecalculating;
            const hasFetchedLog = Object.prototype.hasOwnProperty.call(
              fetchedLogs,
              b.id,
            );
            const log = canShowLog
              ? hasFetchedLog
                ? fetchedLogs[b.id]
                : staleLogIds.has(b.id)
                  ? null
                  : resolveBlockLog(b, logs)
              : null;
            const inputLabelByMeta = buildInputLabelByMeta(b.dependencies);
            const showDevError = effectiveStatus === "failed" && blockError;
            const noPeriod =
              !periodCtx?.currentYear || !periodCtx?.currentMonth;
            const isRecalcDisabled = isRecalculating || noPeriod;
            return (
              <div key={b.id} className={classes.blockItem}>
                <div
                  className={`${classes.blockRow} ${
                    isOpen ? classes.blockRowOpen : ""
                  }`}
                  onClick={() => toggle(b)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(b);
                    }
                  }}
                >
                  <div>
                    <div className={classes.blockName}>{b.displayName}</div>
                    <div className={classes.blockNameMeta}>{b.description}</div>
                  </div>
                  <div className={classes.blockStatusCluster}>
                    <span
                      className={`${classes.blockStatus} ${statusClass(
                        classes,
                        effectiveStatus,
                      )}`}
                    >
                      {STATUS_LABEL[effectiveStatus] || effectiveStatus}
                    </span>
                    {userIsDev && (
                      <Tooltip title="Recalcular bloco">
                        <span className={classes.blockRecalcTooltipWrap}>
                          <button
                            type="button"
                            className={classes.blockRecalcIconButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isRecalcDisabled) return;
                              handleRecalc(b);
                            }}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                            }}
                            disabled={isRecalcDisabled}
                            aria-label="Recalcular bloco"
                          >
                            {isRecalculating ? (
                              <span className={classes.blockRecalcSpinner} />
                            ) : (
                              <Refresh />
                            )}
                          </button>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {isBlockDone(effectiveStatus) ? (
                    <span className={classes.blockDuration}>
                      <strong>{formatLastRunLocal(b.lastRun) || "—"}</strong>
                      <small>
                        {b.duration ? `execução: ${b.duration}` : ""}
                      </small>
                    </span>
                  ) : (
                    <span className={classes.blockDurationPlaceholder} />
                  )}
                  <KeyboardArrowDown
                    className={`${classes.blockChevron} ${
                      isOpen ? classes.blockChevronOpen : ""
                    }`}
                  />
                </div>
                <div
                  className={`${classes.blockLogWrapper} ${
                    isOpen ? classes.blockLogWrapperOpen : ""
                  }`}
                  aria-hidden={!isOpen}
                >
                  <div className={classes.blockTabs}>
                    <button
                      type="button"
                      className={`${classes.blockTab} ${
                        activeTab === "log" ? classes.blockTabActive : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActiveTabFor(b.id, "log");
                      }}
                    >
                      <SummarizeOutlined />
                      Log do cálculo
                    </button>
                    <button
                      type="button"
                      className={`${classes.blockTab} ${
                        activeTab === "deps" ? classes.blockTabActive : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActiveTabFor(b.id, "deps");
                      }}
                    >
                      <AccountTreeOutlined />
                      Dependências
                    </button>
                    {userIsDev && (
                      <button
                        type="button"
                        className={`${classes.blockTab} ${
                          activeTab === "dev" ? classes.blockTabActive : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActiveTabFor(b.id, "dev");
                        }}
                      >
                        <BugReportOutlined />
                        Detalhe DEV
                      </button>
                    )}
                  </div>
                  {activeTab && (
                    <div className={classes.blockTabPanel}>
                      <div className={classes.blockTabPanelHead}>
                        <div className={classes.blockDepsTitle}>
                          {activeTab === "deps"
                            ? "Dependências"
                            : activeTab === "dev"
                              ? "Detalhe técnico"
                              : "Log do cálculo"}
                        </div>
                      </div>
                      {activeTab === "log" &&
                        (isRecalculating ? (
                          <PaneLoadingBody
                            classes={classes}
                            clientColor={clientColor}
                            message="Solicitando recálculo do bloco…"
                          />
                        ) : !canShowLog ? (
                          <div className={classes.blockLogPanelEmpty}>
                            <DescriptionOutlined />
                            <span>
                              O log do cálculo aparece apenas depois que o bloco
                              estiver concluído.
                            </span>
                          </div>
                        ) : log ? (
                          <BlockLogViewer
                            logData={log}
                            currentYear={periodCtx?.currentYear}
                            currentMonth={periodCtx?.currentMonth}
                            firstYear={periodCtx?.firstYear}
                            firstMonth={periodCtx?.firstMonth}
                            lastYear={periodCtx?.lastYear}
                            lastMonth={periodCtx?.lastMonth}
                            onOpenInput={onOpenInput}
                            inputLabelByMeta={inputLabelByMeta}
                          />
                        ) : logLoading ? (
                          <PaneLoadingBody
                            classes={classes}
                            clientColor={clientColor}
                            message="Carregando log do cálculo…"
                          />
                        ) : (
                          <div className={classes.blockLogPanelEmpty}>
                            <DescriptionOutlined />
                            <span>Sem log disponível para este bloco.</span>
                          </div>
                        ))}
                      {activeTab === "deps" && (
                        <DependenciesMatrix
                          classes={classes}
                          dependencies={b.dependencies}
                          periodCtx={periodCtx}
                        />
                      )}
                      {activeTab === "dev" && userIsDev && (
                        <>
                          {showDevError ? (
                            <div className={classes.blockErrorPanel}>
                              {blockError}
                            </div>
                          ) : (
                            <div className={classes.blockLogPanelEmpty}>
                              <BugReportOutlined />
                              <span>
                                Detalhe técnico disponível apenas para blocos
                                com falha.
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

BlocksPane.propTypes = {
  blocks: PropTypes.array,
  blockLogs: PropTypes.object,
  // Contexto de periodos para o ``BlockLogViewer`` (mes "atual" + range
  // disponivel da implementacao). Quando ``undefined``, o
  // ``BlockLogViewer`` usa seus defaults internos — util para
  // desenvolvimento isolado.
  periodCtx: PropTypes.shape({
    currentYear: PropTypes.number,
    currentMonth: PropTypes.number,
    firstYear: PropTypes.number,
    firstMonth: PropTypes.number,
    lastYear: PropTypes.number,
    lastMonth: PropTypes.number,
  }),
  onOpenInput: PropTypes.func,
  // Callback opcional disparado quando o backend devolve um
  // ``monthStatus`` canonico no payload de ``/blocks/`` — usado pela
  // ``InputsV3`` para sobrepor ``selectedMonthCanonical`` no mesmo
  // tick em que o tile do bloco transiciona, garantindo paridade
  // visual entre o badge do topo e a aba "Calculos".
  onMonthStatusChange: PropTypes.func,
  // ``monthStatusHint`` (leva 101): status canonico do mes vindo do
  // ``useProgressOverviewV3`` (endpoint do badge do topo). Quando o
  // hint sinaliza drift (``stale``, ``processing``, etc.) o hook
  // ``useBlocksV3`` faz refetch imediato sem esperar a cadencia lenta.
  monthStatusHint: PropTypes.string,
  // Onda 2.0: payload completo do ``useInputsStateV3`` (substitui o
  // polling local de ``useBlocksV3`` quando presente). Shape:
  // ``{ blocks, summary, monthStatus, monthStatusReason,
  //     monthStatusActive, snapshotSource, userIsDev, refetch }``.
  blocksOverride: PropTypes.shape({
    blocks: PropTypes.array,
    summary: PropTypes.object,
    monthStatus: PropTypes.string,
    monthStatusReason: PropTypes.string,
    monthStatusActive: PropTypes.object,
    snapshotSource: PropTypes.object,
    userIsDev: PropTypes.bool,
    refetch: PropTypes.func,
  }),
};

export default BlocksPane;
